import { useMemo } from 'react'
import type {
  CanvasPosition,
  CanvasStateOutput,
  EndpointDisplayInfo,
  EndpointDisplayType,
  LayoutCalculationResult,
  MapSetResult,
  TileSize,
} from '../../types/infrastructure-map'

interface DisplayContext {
  endpointId: string
  isInMapSet: boolean
  hasLinks: boolean
  canvasState: CanvasStateOutput | undefined
}

interface DisplayBase {
  displayType: EndpointDisplayType
  tileSize: TileSize
  position: CanvasPosition | null
}

function basePositionFromCanvas(canvasState: CanvasStateOutput | undefined): CanvasPosition | null {
  if (!canvasState) return null
  return { x: canvasState.positionX, y: canvasState.positionY }
}

function computeDisplayBase(context: DisplayContext): DisplayBase {
  const { isInMapSet, hasLinks, canvasState } = context
  if (isInMapSet) {
    return {
      displayType: 'tile',
      tileSize: canvasState?.tileSize ?? 'small',
      position: basePositionFromCanvas(canvasState),
    }
  }
  if (hasLinks) {
    return {
      displayType: 'dot',
      tileSize: 'dot',
      position: basePositionFromCanvas(canvasState),
    }
  }
  return { displayType: 'orphan-list', tileSize: 'small', position: null }
}

interface ApplyLayoutNodeArgs {
  base: DisplayBase
  layoutResult: LayoutCalculationResult | null
  endpointId: string
}

function applyLayoutNode(args: ApplyLayoutNodeArgs): {
  position: CanvasPosition | null
  lanGroupId: number | null
} {
  const { base, layoutResult, endpointId } = args
  const layoutNode = layoutResult?.nodes.find((node) => node.endpointId === endpointId)
  if (!layoutNode) {
    return { position: base.position, lanGroupId: null }
  }
  const position = base.position ?? { x: layoutNode.positionX, y: layoutNode.positionY }
  return { position, lanGroupId: layoutNode.lanGroupId }
}

interface BuildEndpointInfoArgs {
  endpointId: string
  mapSet: MapSetResult
  linkedEndpointIds: Set<string>
  canvasStatesMap: Map<string, CanvasStateOutput>
  layoutResult: LayoutCalculationResult | null
  findCollapsedAncestor: (endpointId: string) => string | null
}

function buildEndpointInfo(args: BuildEndpointInfoArgs): EndpointDisplayInfo {
  const {
    endpointId,
    mapSet,
    linkedEndpointIds,
    canvasStatesMap,
    layoutResult,
    findCollapsedAncestor,
  } = args
  const isInMapSet = mapSet.isInSet(endpointId)
  const hasLinks = linkedEndpointIds.has(endpointId)
  const canvasState = canvasStatesMap.get(endpointId)
  const isCollapsed = canvasState?.isCollapsed ?? false
  const collapsedParentId = findCollapsedAncestor(endpointId)
  const isHiddenByCollapse = collapsedParentId !== null

  const base = computeDisplayBase({ endpointId, isInMapSet, hasLinks, canvasState })
  const { position, lanGroupId } = applyLayoutNode({ base, layoutResult, endpointId })

  return {
    endpointId,
    displayType: base.displayType,
    tileSize: base.tileSize,
    position,
    lanGroupId,
    isInMapSet,
    hasLinks,
    isCollapsed,
    isHiddenByCollapse,
    collapsedParentId,
  }
}

export interface UseDisplayInfoOptions {
  endpointIds: string[]
  mapSet: MapSetResult
  linkedEndpointIds: Set<string>
  canvasStatesMap: Map<string, CanvasStateOutput>
  layoutResult: LayoutCalculationResult | null
  findCollapsedAncestor: (endpointId: string) => string | null
}

export interface UseDisplayInfoResult {
  displayInfo: Map<string, EndpointDisplayInfo>
  orphanIds: string[]
}

export function useDisplayInfo(options: UseDisplayInfoOptions): UseDisplayInfoResult {
  const {
    endpointIds,
    mapSet,
    linkedEndpointIds,
    canvasStatesMap,
    layoutResult,
    findCollapsedAncestor,
  } = options

  const displayInfo = useMemo(() => {
    const info = new Map<string, EndpointDisplayInfo>()
    for (const endpointId of endpointIds) {
      info.set(
        endpointId,
        buildEndpointInfo({
          endpointId,
          mapSet,
          linkedEndpointIds,
          canvasStatesMap,
          layoutResult,
          findCollapsedAncestor,
        })
      )
    }
    return info
  }, [endpointIds, mapSet, linkedEndpointIds, canvasStatesMap, layoutResult, findCollapsedAncestor])

  const orphanIds = useMemo(
    () =>
      Array.from(displayInfo.entries())
        .filter(([, info]) => info.displayType === 'orphan-list')
        .map(([id]) => id),
    [displayInfo]
  )

  return { displayInfo, orphanIds }
}
